import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  LayoutDashboard,
  Wifi,
  Building2,
  Bell,
  BellDot,
  Users,
  Settings,
  ChevronDown,
  ChevronRight,
  Star,
  List,
  Shield,
  Activity,
  BarChart3,
  Cpu,
  FileText,
  Key
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { ROUTES } from '@/config/routes'
import { useFavoriteDevices } from '@/hooks/useDevices'
import { alertService } from '@/services/alert.service'
import { useAuthStore } from '@/stores/authStore'

interface NavItem {
  label: string
  path?: string
  icon: React.ComponentType<{ className?: string }>
  children?: NavItem[]
}

const baseNavItems: NavItem[] = [
  {
    label: 'Dashboard',
    path: ROUTES.DASHBOARD,
    icon: LayoutDashboard
  },
  {
    label: 'Devices',
    icon: Wifi,
    children: [
      {
        label: 'All Devices',
        path: ROUTES.DEVICES,
        icon: List
      },
      {
        label: 'Favourites',
        path: '/devices/favorites',
        icon: Star
      }
    ]
  },
  {
    label: 'Accounts',
    path: ROUTES.ACCOUNTS,
    icon: Building2
  },
  {
    label: 'Alerts',
    path: ROUTES.ALERTS,
    icon: Bell
  },
  {
    label: 'Alert History',
    path: '/alerts/history',
    icon: BellDot
  },
  {
    label: 'Users',
    path: ROUTES.USERS,
    icon: Users
  },
  {
    label: 'Settings',
    path: ROUTES.SETTINGS,
    icon: Settings
  }
]

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
}

export const Sidebar = ({ isOpen, onClose }: SidebarProps) => {
  const location = useLocation()
  const user = useAuthStore(state => state.user)
  // Only expand Devices by default on desktop (md and up)
  const [expandedItems, setExpandedItems] = useState<string[]>(() => {
    return typeof window !== 'undefined' && window.innerWidth >= 768 ? ['Devices'] : []
  })
  const { data: favoritesData } = useFavoriteDevices()
  const favoriteDevices = favoritesData?.devices || []

  const { data: activeAlertCount = 0 } = useQuery({
    queryKey: ['triggered-alerts-count'],
    queryFn: () => alertService.getTriggeredAlerts(false),
    select: (data) => data.total,
    staleTime: 30000,
    refetchInterval: 30000,
  })

  const adminNavItem: NavItem = {
    label: 'Administration',
    icon: Shield,
    children: [
      { label: 'NOC Dashboard', path: '/admin/noc', icon: Activity },
      { label: 'Metrics', path: '/dashboard/metrics', icon: BarChart3 },
      { label: 'Device Types', path: '/admin/device-types', icon: Cpu },
      { label: 'System Logs', path: '/admin/logs', icon: FileText },
      { label: 'Claim Codes', path: '/devices/claim-codes', icon: Key },
    ]
  }

  const navItems = user?.isSuperUser ? [...baseNavItems, adminNavItem] : baseNavItems

  const toggleExpanded = (label: string) => {
    setExpandedItems(prev =>
      prev.includes(label)
        ? prev.filter(item => item !== label)
        : [...prev, label]
    )
  }

  const renderNavItem = (item: NavItem) => {
    const Icon = item.icon
    const hasChildren = item.children && item.children.length > 0
    const isExpanded = expandedItems.includes(item.label)
    const isActive = item.path ? (location.pathname === item.path || location.pathname.startsWith(item.path + '/')) : false

    if (hasChildren) {
      return (
        <div key={item.label}>
          <button
            onClick={() => toggleExpanded(item.label)}
            className={cn(
              'flex items-center gap-3 rounded-lg px-3 py-2 transition-colors w-full',
              'hover:bg-accent hover:text-accent-foreground'
            )}
          >
            <Icon className="h-5 w-5 flex-shrink-0" />
            <span className={cn(
              'flex-1 text-left transition-opacity duration-300',
              isOpen ? 'opacity-100' : 'opacity-0 md:opacity-0'
            )}>{item.label}</span>
            {isOpen && (
              isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />
            )}
          </button>

          {isExpanded && isOpen && (
            <div className="ml-4 mt-1 space-y-1">
              {item.children?.map((child) => {
                const ChildIcon = child.icon
                const isChildActive = child.path ? (location.pathname === child.path || location.pathname.startsWith(child.path + '/')) : false

                return (
                  <Link
                    key={child.path || child.label}
                    to={child.path!}
                    onClick={() => {
                      if (window.innerWidth < 768) {
                        onClose()
                      }
                    }}
                    className={cn(
                      'flex items-center gap-3 rounded-lg px-3 py-2 transition-colors text-sm',
                      isChildActive
                        ? 'bg-primary text-primary-foreground'
                        : 'hover:bg-accent hover:text-accent-foreground'
                    )}
                  >
                    <ChildIcon className="h-4 w-4 flex-shrink-0" />
                    <span>{child.label}</span>
                  </Link>
                )
              })}

              {/* Show favorite devices under Favourites - only on desktop or fewer on mobile */}
              {item.label === 'Devices' && favoriteDevices.length > 0 && (
                <div className="ml-4 mt-2 space-y-1">
                  {favoriteDevices.slice(0, window.innerWidth < 768 ? 3 : 5).map((device) => (
                    <Link
                      key={device.id}
                      to={`/devices/${device.id}`}
                      onClick={() => {
                        if (window.innerWidth < 768) {
                          onClose()
                        }
                      }}
                      className={cn(
                        'flex items-center gap-2 rounded-lg px-3 py-1.5 transition-colors text-xs',
                        location.pathname === `/devices/${device.id}`
                          ? 'bg-primary/10 text-primary'
                          : 'hover:bg-accent hover:text-accent-foreground text-muted-foreground'
                      )}
                    >
                      <Star className="h-3 w-3 flex-shrink-0 fill-current" />
                      <span className="truncate max-w-[180px]">{device.name}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )
    }

    return (
      <Link
        key={item.path}
        to={item.path!}
        onClick={() => {
          if (window.innerWidth < 768) {
            onClose()
          }
        }}
        className={cn(
          'flex items-center gap-3 rounded-lg px-3 py-2 transition-colors',
          isActive
            ? 'bg-primary text-primary-foreground'
            : 'hover:bg-accent hover:text-accent-foreground'
        )}
      >
        <Icon className="h-5 w-5 flex-shrink-0" />
        <span className={cn(
          'transition-opacity duration-300',
          isOpen ? 'opacity-100' : 'opacity-0 md:opacity-0'
        )}>{item.label}</span>
        {item.label === 'Alert History' && activeAlertCount > 0 && (
          <span className="ml-auto bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[20px] text-center">
            {activeAlertCount}
          </span>
        )}
      </Link>
    )
  }

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/50 md:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed md:static inset-y-0 left-0 z-30 border-r bg-white transition-all duration-300 overflow-hidden',
          'md:translate-x-0',
          isOpen ? 'translate-x-0 w-64' : '-translate-x-full md:translate-x-0 md:w-20'
        )}
      >
        <nav className="space-y-2 p-4 overflow-y-auto h-full pb-safe">
          {navItems.map(renderNavItem)}
        </nav>
      </aside>
    </>
  )
}
